import { FormStore, GlobalStore } from "@roc/feature-app-core";
import { PayoffService } from "../services/payoffService";
import { action, flow, makeObservable, observable } from "mobx";
import { DocumentStore } from "@roc/feature-documents";
import { LoanStore } from "@roc/feature-loans";

const form = {
  fields: {
    loanId: {
      value: '',
      error: null,
      rule: '',
    },
    description: {
      value: '',
      error: null,
      rule: 'required',
    },
    origAmt: {
      value: '',
      error: null,
      rule: 'required',
    },
  },
  meta: {
    isValid: false,
    error: null,
  },
};

export interface ServicerFees {
  description?: string,
  origAmt?: number,
}

export class ServicerFeeStore extends FormStore {
  private documentStore: DocumentStore;
  private globalStore: GlobalStore;
  loanStore: LoanStore;
  payoffService: PayoffService;
  loanId: string;
  servicerFees: ServicerFees[];
  currentIndex: number;
  currentFee: any;
  foreclosure: boolean;

  constructor(
    loanStore: LoanStore,
    documentStore: DocumentStore,
    globalStore: GlobalStore
  ) {
    super({ ...form }, globalStore);
    this.documentStore = documentStore;
    this.globalStore = globalStore;
    this.loanStore = loanStore;
    this.payoffService = new PayoffService();
    this.currentIndex = null;

    makeObservable(this, {
      loanId: observable,
      foreclosure: observable,
      setLoanId: action,
      getServicerFees: flow,
      saveServicerFees: flow,
      servicerFees: observable,
      currentIndex: observable,
      currentFee: observable,
      addFee: action,
      editFee: action,
      deleteFee: action,
    });
  }

  initialize() {
    this.resetStore();
  }

  setLoanId(loanId: string) {
    this.loanId = loanId;
  }

  getFeeList(): ServicerFees[] {
    return this.servicerFees;
  }

  *getServicerFees(loan: any) {
    try {
      const response = yield this.payoffService.getPayoffData(loan.loanId);
      const data = response.data.data;
      if (data && data.servicerFeesJson) {
        const parsedFees = JSON.parse(data.servicerFeesJson);
        this.servicerFees = parsedFees && parsedFees != undefined ? parsedFees : [];
      } else if (data) {
        this.foreclosure = data.foreclosure;
        if (data.foreclosure) {
          const resp = yield this.payoffService.getForeclosureCharges(loan.loanId);
          this.servicerFees = resp.data.data;
        } else {
          const resp = yield this.payoffService.getTdsCharges(loan.loanId);
          this.servicerFees = resp.data.data;
        }
      }
      return this.servicerFees;
    } catch (e) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'An error occurred while getting servicer fees',
      });
    }
  }

  addFee() {
    this.runFormValidationWithMessage();
    const feeList = this.servicerFees.concat();
    if (this.form.meta.isValid) {
      const fee = this.getFormValues();
      feeList.push(fee);
      this.servicerFees = feeList;
      return true;
    } else {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Please fix the validation errors',
      });
      return false;
    }
  }

  editFee() {
    this.runFormValidationWithMessage();
    const feeList = this.servicerFees.concat();
    if (this.form.meta.isValid) {
      if (this.currentIndex > -1 && this.currentIndex < this.servicerFees.length) {
        const fee = this.getFormValues();
        feeList.splice(this.currentIndex, 1, fee);
      }
      this.servicerFees = feeList;
      return true;
    } else {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Please fix the validation errors',
      });
      return false;
    }
  }

  deleteFee(index: number) {
    if (index > -1 && index < this.servicerFees.length) {
      const feeList = this.servicerFees.concat();
      feeList.splice(index, 1);
      this.servicerFees = feeList;
    }
  }

  *saveServicerFees(fees) {
    try {
      yield this.payoffService.saveServicerFees(this.loanId, fees);
      yield this.documentStore.documentFormStore.closeCurrentForm();
      yield this.loanStore.refreshLoanDetails();
      this.globalStore.notificationStore.showSuccessNotification({
        message: 'Servicer fees submitted successfully',
      });
    } catch (e) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'An error occurred while submitting servicer fees',
      });
    }
  }

  setCurrentSelectedIndex(currentSelectedIndex) {
    this.currentIndex = currentSelectedIndex;
  }

  setCurrentSelectedFee(fee) {
    this.currentFee = fee;
    if (fee) {
      this.onFieldChange('description', fee?.description);
      this.onFieldChange('origAmt', fee?.origAmt);
    } else {
      this.reset();
    }
  }

  resetStore() {
    this.reset();
    this.loanId = null;
    this.servicerFees = [];
    this.currentIndex = null;
    this.currentFee = null;
  }
}

export default ServicerFeeStore;